import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { format, addDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicationManager = ({ isOpen, onClose, patientId, onUpdate, editingMedication = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    purpose: '',
    dosage: '',
    frequency: 'daily',
    duration: '30 days',
    customDuration: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    reminder_times: ['09:00'],
    weekdays: [],
    notes: '',
  });

  // Update form when editingMedication changes
  useEffect(() => {
    if (editingMedication) {
      setFormData({
        name: editingMedication.name || '',
        purpose: editingMedication.purpose || '',
        dosage: editingMedication.dosage || '',
        frequency: editingMedication.frequency || 'daily',
        duration: editingMedication.duration || '30 days',
        customDuration: '',
        start_date: editingMedication.start_date || format(new Date(), 'yyyy-MM-dd'),
        reminder_times: editingMedication.reminder_times || ['09:00'],
        weekdays: editingMedication.weekdays || [],
        notes: editingMedication.notes || '',
      });
    } else {
      // Reset form for new medication
      setFormData({
        name: '',
        purpose: '',
        dosage: '',
        frequency: 'daily',
        duration: '30 days',
        customDuration: '',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        reminder_times: ['09:00'],
        weekdays: [],
        notes: '',
      });
    }
  }, [editingMedication, isOpen]);

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'twice-daily', label: 'Twice a Day' },
    { value: 'three-times-weekly', label: 'Three Times a Week' },
    { value: 'every-other-day', label: 'Every Other Day' },
    { value: 'custom', label: 'Custom' },
  ];

  const durationOptions = [
    { value: '5 days', label: '5 days' },
    { value: '10 days', label: '10 days' },
    { value: '30 days', label: '30 days' },
    { value: '90 days', label: '90 days' },
    { value: 'custom', label: 'Custom' },
  ];

  const weekdayOptions = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  const calculateEndDate = () => {
    if (!formData.start_date) return '';
    
    let days = 30;
    if (formData.duration === 'custom') {
      days = parseInt(formData.customDuration) || 30;
    } else {
      days = parseInt(formData.duration.split(' ')[0]);
    }
    
    // Subtract 1 because start_date is day 1
    // For 5 days: start_date + 4 days = 5 total days
    const endDate = addDays(new Date(formData.start_date), days - 1);
    return format(endDate, 'yyyy-MM-dd');
  };

  const handleFrequencyChange = (frequency) => {
    let reminderTimes = ['09:00'];
    if (frequency === 'twice-daily') {
      reminderTimes = ['09:00', '21:00'];
    }
    setFormData({ ...formData, frequency, reminder_times: reminderTimes });
  };

  const handleWeekdayToggle = (day) => {
    const updatedWeekdays = formData.weekdays.includes(day)
      ? formData.weekdays.filter(d => d !== day)
      : [...formData.weekdays, day];
    setFormData({ ...formData, weekdays: updatedWeekdays });
  };

  const addReminderTime = () => {
    setFormData({
      ...formData,
      reminder_times: [...formData.reminder_times, '12:00']
    });
  };

  const updateReminderTime = (index, value) => {
    const updatedTimes = [...formData.reminder_times];
    updatedTimes[index] = value;
    setFormData({ ...formData, reminder_times: updatedTimes });
  };

  const removeReminderTime = (index) => {
    if (formData.reminder_times.length > 1) {
      const updatedTimes = formData.reminder_times.filter((_, i) => i !== index);
      setFormData({ ...formData, reminder_times: updatedTimes });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    const medicationData = {
      patient_id: patientId,
      name: formData.name,
      purpose: formData.purpose,
      dosage: formData.dosage,
      frequency: formData.frequency,
      duration: formData.duration === 'custom' ? `${formData.customDuration} days` : formData.duration,
      start_date: formData.start_date,
      end_date: calculateEndDate(),
      reminder_times: formData.reminder_times,
      weekdays: formData.weekdays,
      notes: formData.notes,
    };

    try {
      if (editingMedication) {
        // Update existing medication
        await axios.put(
          `${API}/medications/${editingMedication.id}`,
          medicationData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Medication updated successfully');
      } else {
        // Create new medication
        await axios.post(
          `${API}/medications`,
          medicationData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Medication added successfully');
      }
      
      onClose();
      setFormData({
        name: '',
        purpose: '',
        dosage: '',
        frequency: 'daily',
        duration: '30 days',
        customDuration: '',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        reminder_times: ['09:00'],
        weekdays: [],
        notes: '',
      });
      onUpdate();
    } catch (error) {
      toast.error(editingMedication ? 'Failed to update medication' : 'Failed to add medication');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingMedication ? 'Edit Medication' : 'Add Medication'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="medication-manager-form">
          {/* Row 1: Medication | For what */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Medication *</label>
              <input
                data-testid="med-name-input"
                type="text"
                className="input w-full"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Aspirin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">For What</label>
              <input
                type="text"
                className="input w-full"
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                placeholder="e.g., Pain relief, Nausea"
              />
            </div>
          </div>

          {/* Row 2: Dosage | Frequency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Dosage *</label>
              <input
                data-testid="med-dosage-input"
                type="text"
                className="input w-full"
                value={formData.dosage}
                onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                required
                placeholder="e.g., 500mg, 1 tablet"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Frequency *</label>
              <select
                className="input w-full"
                value={formData.frequency}
                onChange={(e) => handleFrequencyChange(e.target.value)}
                required
              >
                {frequencyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Show weekday selector for specific frequencies */}
          {formData.frequency === 'three-times-weekly' && (
            <div>
              <label className="block text-sm font-medium mb-2">Select Days</label>
              <div className="grid grid-cols-3 gap-2">
                {weekdayOptions.map(day => (
                  <div key={day} className="flex items-center space-x-2">
                    <Checkbox
                      data-testid={`weekday-${day}`}
                      checked={formData.weekdays.includes(day)}
                      onCheckedChange={() => handleWeekdayToggle(day)}
                      id={day}
                    />
                    <label htmlFor={day} className="text-sm cursor-pointer">{day}</label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Duration</label>
              <select
                data-testid="med-duration-select"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                className="w-full"
              >
                {durationOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {formData.duration === 'custom' && (
                <input
                  data-testid="med-custom-duration-input"
                  type="number"
                  value={formData.customDuration}
                  onChange={(e) => setFormData({ ...formData, customDuration: e.target.value })}
                  placeholder="Enter number of days"
                  className="mt-2 w-full"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <input
                data-testid="med-start-date-input"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
              {formData.start_date && (
                <p className="text-xs text-gray-600 mt-1">
                  End date: {calculateEndDate()}
                </p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Reminder Time(s)</label>
              {formData.frequency === 'custom' && (
                <button
                  type="button"
                  data-testid="add-reminder-time-button"
                  onClick={addReminderTime}
                  className="text-sm btn btn-primary px-3 py-1"
                >
                  + Add Time
                </button>
              )}
            </div>
            <div className="space-y-2">
              {formData.reminder_times.map((time, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    data-testid={`reminder-time-${index}`}
                    type="time"
                    value={time}
                    onChange={(e) => updateReminderTime(index, e.target.value)}
                    className="flex-1"
                  />
                  {formData.frequency === 'custom' && formData.reminder_times.length > 1 && (
                    <button
                      type="button"
                      data-testid={`remove-reminder-time-${index}`}
                      onClick={() => removeReminderTime(index)}
                      className="text-red-500 text-sm px-2"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes field */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Notes <span className="text-gray-500 text-xs">(e.g., "Take 5 days after each chemo")</span>
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any additional notes or instructions..."
              className="input w-full"
              rows="3"
            />
          </div>

          <button
            data-testid="submit-medication-button"
            type="submit"
            className="btn btn-primary w-full"
          >
            {editingMedication ? 'Update Medication' : 'Add Medication'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MedicationManager;
